import {
    ButtonGradientPrimary,
    ButtonPrimary,
    Colors,
    Icon,
    PageContainer,
    SearchBar,
} from '@amelio/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BatchAssignmentStep } from './components/BatchAssignmentStep';
import { EmployeeSection } from './components/EmployeeSection';
import {
    buildCreateProfileWizardSearch,
    buildCreateProfileWizardLocationState,
    CreateProfileReturnMode,
} from '../Competencies/createProfileWizardNavigation';

import type {
    RosterEmployee,
    RosterFilterParams,
} from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { AssignProfileLocationState } from '@/navigation/assignProfileNavigation';

import { useGetCompetencyProfiles } from '@/api/competency-services/competency-profile';
import { useGetRoster } from '@/api/competency-services/profile-assignment';
import { isValidProfileId } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { useGetCompanyInformation, useGetUserPermissions } from '@/api/directory';
import { CompanyAttributeFilters } from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import {
    EmployeeCompetencyAssignmentFlow,
    MUTED,
    NAVY,
} from '@/components/CompetencyProfileAssignment';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { resolveAssignProfileRequest } from '@/navigation/assignProfileNavigation';
import { RouteName } from '@/navigation/types';
import { toRosterParams } from '@/utils/companyAttributeFilters';

// The flow the page is currently in. `list` shows the roster; the others replace the
// list with a full-page step view inside the same shell (no modal).
type FlowState =
    | { kind: 'list' }
    | { kind: 'assign'; employee: RosterEmployee; initialMode: 'choice' | 'selector' }
    | { kind: 'batch' };

export const CompetencyProfilesAdminPage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const location = useLocation<AssignProfileLocationState | undefined>();

    const { data: company } = useGetCompanyInformation();
    const { data: permissions } = useGetUserPermissions();
    const { data: profilesData } = useGetCompetencyProfiles();

    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<SelectedFilters>();
    const [pendingPage, setPendingPage] = useState(1);
    const [assignedPage, setAssignedPage] = useState(1);
    const [flow, setFlow] = useState<FlowState>({ kind: 'list' });
    const hasOpenedRequestedEmployeeRef = useRef(false);

    const assignRequest = resolveAssignProfileRequest({
        locationState: location.state,
        search: location.search,
    });
    const requestedAssignEmployeeId = assignRequest?.assignEmployeeId;

    // Entering a step view (or returning to the list) should always start at the top.
    // An in-page view swap keeps the host's scroll position otherwise, so the choice/
    // selector/batch step would open mid-page where the clicked tile happened to be.
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0 });
    }, [flow.kind]);

    const rosterParams = useMemo<RosterFilterParams>(
        () =>
            filters
                ? toRosterParams(filters, search)
                : {
                      searchQuery: search.trim() || undefined,
                      isActiveOnly: true,
                      includeInvited: true,
                  },
        [filters, search],
    );

    // Unfiltered roster for copy candidates: all employees with profiles regardless of department filter
    const unfilteredRosterParams = useMemo<RosterFilterParams>(
        () => ({ isActiveOnly: true, includeInvited: true }),
        [],
    );

    const { data: roster, isLoading, isError, refetch } = useGetRoster(rosterParams);
    const { data: unfilteredRoster } = useGetRoster(unfilteredRosterParams);

    const employees = useMemo(() => roster ?? [], [roster]);
    const profiles = useMemo(
        () => (profilesData ?? []).filter((profile) => profile.isActive),
        [profilesData],
    );

    const withoutProfile = useMemo(
        () => employees.filter((employee) => !isValidProfileId(employee.assignedProfileId)),
        [employees],
    );
    const withProfile = useMemo(
        () => employees.filter((employee) => isValidProfileId(employee.assignedProfileId)),
        [employees],
    );

    // Copy sources: all employees with profiles across the company (unfiltered by department)
    const allWithProfile = useMemo(
        () =>
            (unfilteredRoster ?? []).filter((employee) =>
                isValidProfileId(employee.assignedProfileId),
            ),
        [unfilteredRoster],
    );

    const copyCandidates = useCallback(
        (excludeId: string) => allWithProfile.filter((employee) => employee.id !== excludeId),
        [allWithProfile],
    );

    const onFiltersChange = useCallback((next: SelectedFilters) => {
        setFilters(next);
        setPendingPage(1);
        setAssignedPage(1);
    }, []);

    const openEmployee = useCallback((employee: RosterEmployee) => {
        // No profile → choice step; has a profile → straight to the selector in edit mode.
        setFlow({
            kind: 'assign',
            employee,
            initialMode: isValidProfileId(employee.assignedProfileId) ? 'selector' : 'choice',
        });
    }, []);

    // Arrived from a flow blocked by a missing profile (e.g. picking an employee for a manager
    // assessment): open that employee's assignment step as soon as the roster resolves. Fires
    // once, so cancelling back to the list is not immediately undone.
    useEffect(() => {
        if (hasOpenedRequestedEmployeeRef.current || !requestedAssignEmployeeId) {
            return;
        }

        const requested = employees.find((employee) => employee.id === requestedAssignEmployeeId);
        if (!requested) {
            return;
        }

        hasOpenedRequestedEmployeeRef.current = true;
        openEmployee(requested);
    }, [requestedAssignEmployeeId, employees, openEmployee]);

    const closeFlow = () => setFlow({ kind: 'list' });

    // Only the employee the deep link asked for carries a way back; anyone opened from the
    // roster afterwards belongs to this page.
    const returnPathFor = (employeeId: string) =>
        assignRequest?.assignEmployeeId === employeeId ? assignRequest.returnTo : undefined;

    // Leaving an employee opened from another module returns there, whether the profile was
    // assigned or the user backed out, instead of falling through to an unrequested roster.
    const leaveAssignFlow = () => {
        const employeeId = flow.kind === 'assign' ? flow.employee.id : undefined;
        const returnTo = employeeId ? returnPathFor(employeeId) : undefined;
        if (returnTo) {
            navigate({
                path: returnTo,
                state: { preselectedEmployeeId: employeeId },
            });
            return;
        }

        closeFlow();
    };

    // Reuse the existing guided wizard. We pass the originating employee id via router
    // state so the wizard can return the user to this roster (rather than the
    // Competencies profiles tab) once the profile is created.
    // Goes through `navigate` so the host router sees the wizard route too: the wizard leaves
    // through the host, and a host push onto an unchanged location replays no popstate.
    const goToCreateProfile = (employeeId: string, employeeName: string) => {
        const wizardState = buildCreateProfileWizardLocationState({
            returnMode: CreateProfileReturnMode.CompetencyProfilesAdmin,
            returnEmployeeId: employeeId,
            returnEmployeeName: employeeName,
            returnPath: returnPathFor(employeeId),
        });
        navigate({
            path: `${RouteName.CompetencyProfileCreate}${buildCreateProfileWizardSearch(wizardState)}`,
            state: wizardState,
        });
    };

    const rosterSections = (
        <>
            {withoutProfile.length > 0 && (
                <EmployeeSection
                    title={t('competencies.competencyProfilesAdmin.sections.pending')}
                    countLabel={t('competencies.competencyProfilesAdmin.sections.pendingCount', {
                        count: withoutProfile.length,
                    })}
                    icon={'essential-info-circle'}
                    iconBgClassName={'bg-amber-100'}
                    iconColor={Colors.WARNING}
                    employees={withoutProfile}
                    page={pendingPage}
                    onPageChange={setPendingPage}
                    onSelectEmployee={openEmployee}
                    testId={'roster-section-pending'}
                />
            )}

            {withProfile.length > 0 && (
                <EmployeeSection
                    title={t('competencies.competencyProfilesAdmin.sections.assigned')}
                    countLabel={t('competencies.competencyProfilesAdmin.sections.assignedCount', {
                        count: withProfile.length,
                    })}
                    icon={'essential-tick-circle'}
                    iconBgClassName={'bg-green-100'}
                    iconColor={Colors.SUCCESS_800}
                    employees={withProfile}
                    page={assignedPage}
                    onPageChange={setAssignedPage}
                    onSelectEmployee={openEmployee}
                    testId={'roster-section-assigned'}
                />
            )}
        </>
    );

    return (
        // Full-page like the 360 dashboard: PageContainer is a <div> (not <main>), so it
        // escapes the global `#root main { max-width: 1080px }` cap that was boxing this
        // page with a grey host-background margin. The inner flex column reproduces the
        // vertical rhythm the <main> used to provide.
        <PageContainer className={'p-[16px] md:px-[44px] md:py-[18px]'}>
            <div className={'ds-gap-3xl flex flex-col'}>
                {/* Header + controls bar stay visible above both the roster list and the
                batch card (matching the prototype). Only the CONTENT below swaps. */}
                {(flow.kind === 'list' || flow.kind === 'batch') && (
                    <>
                        <div className={'mb-5'}>
                            <h1
                                className={'ds-mb-xs text-[36px] font-medium'}
                                style={{ color: NAVY }}
                            >
                                {t('competencies.competencyProfilesAdmin.title')}
                            </h1>

                            <p className={'text-navy/70 text-body-lg'}>
                                {t('competencies.competencyProfilesAdmin.subtitle')}
                            </p>
                        </div>

                        <div className={'roster-controls gap-4'}>
                            <CompanyAttributeFilters
                                company={company}
                                onFiltersChange={onFiltersChange}
                            />

                            <div className={'roster-search'}>
                                <SearchBar
                                    size={'md'}
                                    value={search}
                                    onChange={(value) => {
                                        setSearch(value);
                                        setPendingPage(1);
                                        setAssignedPage(1);
                                    }}
                                    placeholder={t(
                                        'competencies.competencyProfilesAdmin.searchPlaceholder',
                                    )}
                                    className={'!border-gray-200'}
                                    dataTestId={'roster-search'}
                                />
                            </div>

                            <ButtonGradientPrimary
                                size={'md'}
                                onClick={() => setFlow({ kind: 'batch' })}
                                leftIcon={
                                    <Icon
                                        name={'users-profile-2user'}
                                        size={'sm'}
                                        color={Colors.WHITE}
                                    />
                                }
                                dataTestId={'roster-batch-open'}
                            >
                                {t('competencies.competencyProfilesAdmin.batchAssignment')}
                            </ButtonGradientPrimary>
                        </div>
                    </>
                )}

                {flow.kind === 'list' && (
                    <>
                        {isLoading ? (
                            <div className={'flex h-[320px] items-center justify-center'}>
                                <OverlayLoader isLoading={true} />
                            </div>
                        ) : isError ? (
                            <div
                                className={
                                    'rounded-xl border border-white bg-white/45 p-8 text-center backdrop-blur-sm'
                                }
                            >
                                <p className={'text-error mb-4 text-base'}>
                                    {t('competencies.competencyProfilesAdmin.loadError')}
                                </p>

                                <ButtonPrimary
                                    size={'sm'}
                                    onClick={() => void refetch()}
                                    dataTestId={'roster-retry'}
                                >
                                    {t('competencies.competencyProfilesAdmin.retry')}
                                </ButtonPrimary>
                            </div>
                        ) : employees.length === 0 ? (
                            <div
                                className={
                                    'rounded-xl border border-white bg-white/45 p-8 text-center backdrop-blur-sm'
                                }
                            >
                                <p
                                    className={'mb-1 text-lg font-semibold'}
                                    style={{ color: NAVY }}
                                >
                                    {t('competencies.competencyProfilesAdmin.emptyTitle')}
                                </p>

                                <p
                                    className={'text-base'}
                                    style={{ color: MUTED }}
                                >
                                    {t('competencies.competencyProfilesAdmin.emptyDescription')}
                                </p>
                            </div>
                        ) : (
                            rosterSections
                        )}
                    </>
                )}

                {flow.kind === 'assign' && (
                    <EmployeeCompetencyAssignmentFlow
                        employee={flow.employee}
                        copyCandidates={copyCandidates(flow.employee.id)}
                        initialMode={flow.initialMode}
                        onDone={leaveAssignFlow}
                        onCancel={leaveAssignFlow}
                        onCreateGuided={goToCreateProfile}
                        canManageCompetencies={permissions?.canManageCompetencies ?? false}
                    />
                )}

                {/* Batch assignment (3 steps) — full-page view. */}
                {flow.kind === 'batch' && (
                    <>
                        <BatchAssignmentStep
                            unassignedEmployees={withoutProfile}
                            assignedEmployees={withProfile}
                            profiles={profiles}
                            onClose={closeFlow}
                            onAssigned={() => void refetch()}
                        />

                        {rosterSections}
                    </>
                )}
            </div>
        </PageContainer>
    );
};
